import React from "react";
import { Container, Box, Typography } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import { renderTimeViewClock } from "@mui/x-date-pickers";
import { ReEvent } from "../../interfaces/types";

// Define the props for the EventMultipleDateTimePicker component
interface EventMultipleDateTimePickerProps {
  event: any;
  setEvent: (event: any) => void;
  label: string;
  fieldNameStart: keyof ReEvent | keyof ReEvent["eventConfiguration"];
  fieldNameEnd: keyof ReEvent | keyof ReEvent["eventConfiguration"];
  required?: boolean;
  subfield?: boolean;
}

// Define the EventMultipleDateTimePicker functional component
const EventMultipleDateTimePicker: React.FC<
  EventMultipleDateTimePickerProps
> = ({
  event,
  setEvent,
  label,
  fieldNameStart,
  fieldNameEnd,
  subfield = false,
  required = false,
}) => {
  // Define the date-time format
  const dateTimeFormat = "DD-MM-YYYY HH:mm";
  // Get the start date value from the event or event configuration
  const startDateValue = subfield
    ? event.eventConfiguration[
        fieldNameStart as keyof ReEvent["eventConfiguration"]
      ]
    : event[fieldNameStart as keyof ReEvent];
  // Get the end date value from the event or event configuration
  const endDateValue = subfield
    ? event.eventConfiguration[
        fieldNameEnd as keyof ReEvent["eventConfiguration"]
      ]
    : event[fieldNameEnd as keyof ReEvent];

  return (
    <Container
      disableGutters
      sx={{
        justifyContent: "flex-start",
        display: "flex",
        alignItems: "center",
        mt: 2,
      }}
    >
      <Box display="flex" flex={"33%"} justifyContent={"flex-start"}>
        {/* Display the label */}
        <Typography>{label}:</Typography>
      </Box>
      <Box display="flex" flex={"33%"} justifyContent={"center"}>
        {/* Render the DateTimePicker component for the start date */}
        <DateTimePicker
          label={"Start Date"}
          value={startDateValue ? dayjs(startDateValue) : null}
          onChange={(newValue) =>
            subfield
              ? newValue &&
                setEvent({
                  ...event,
                  eventConfiguration: {
                    ...event.eventConfiguration,
                    [fieldNameStart]: newValue.toDate(),
                  },
                })
              : newValue &&
                setEvent({ ...event, [fieldNameStart]: newValue.toDate() })
          }
          disablePast
          format={dateTimeFormat}
          ampm={false}
          minutesStep={5}
          viewRenderers={{
            hours: renderTimeViewClock,
            minutes: renderTimeViewClock,
            seconds: renderTimeViewClock,
          }}
          slotProps={{
            textField: {
              required: required,
            },
          }}
        />
      </Box>
      <Box display="flex" flex={"33%"} justifyContent={"center"}>
        {/* Render the DateTimePicker component for the end date */}
        <DateTimePicker
          label={"End Date"}
          value={endDateValue ? dayjs(endDateValue) : null}
          onChange={(newValue) =>
            subfield
              ? newValue &&
                setEvent({
                  ...event,
                  eventConfiguration: {
                    ...event.eventConfiguration,
                    [fieldNameEnd]: newValue.toDate(),
                  },
                })
              : newValue &&
                setEvent({ ...event, [fieldNameEnd]: newValue.toDate() })
          }
          disablePast
          format={dateTimeFormat}
          ampm={false}
          minutesStep={5}
          viewRenderers={{
            hours: renderTimeViewClock,
            minutes: renderTimeViewClock,
            seconds: renderTimeViewClock,
          }}
          slotProps={{
            textField: {
              required: required,
            },
          }}
        />
      </Box>
    </Container>
  );
};

export default EventMultipleDateTimePicker;
